import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Panel,
  Switch,
  Tabs,
  TextField,
  ViewHeader,
} from '@spacegame/ui';
import { serialize } from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { useSettings, useSettingsActions } from '../game/SettingsProvider';
import { useAuth } from '../game/auth-context';
import { useTutorial } from '../tutorial';
import { BUILD_INFO } from '../game/build-info';

const MIN_PASSWORD_LENGTH = 8;

function downloadSave(saveJson: string, playerName: string): void {
  const blob = new Blob([saveJson], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `dyson-warfare-${playerName.toLowerCase().replace(/\s+/g, '-')}-${date}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formatBuildTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('de-DE');
}

/**
 * Settings — one place for every device/UI preference, deliberately separate
 * from gameplay state (see `game/settings.ts`). Sectioned the way most
 * strategy/mobile games structure this screen: Darstellung → Barrierefreiheit
 * → Spielverhalten → Ton (audio doesn't exist yet, shown honestly as
 * "kommt noch" rather than a dead toggle pretending to work) → Konto & Daten
 * → Info. New settings should slot into one of these rather than growing a
 * seventh category — that's usually a sign it belongs in an existing one.
 */
export function SettingsView() {
  const { game, authStatus } = useGame();
  const actions = useGameActions();
  const auth = useAuth();
  const settings = useSettings();
  const settingsActions = useSettingsActions();
  const tutorial = useTutorial();
  const [resetConfirming, setResetConfirming] = useState(false);

  const [upgradeFormOpen, setUpgradeFormOpen] = useState(false);
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [upgradePassword, setUpgradePassword] = useState('');
  const [upgradePasswordConfirm, setUpgradePasswordConfirm] = useState('');
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [upgradeFieldErrors, setUpgradeFieldErrors] = useState<Record<string, string>>({});
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);

  const [signingOut, setSigningOut] = useState(false);
  const [anonymousSignOutConfirming, setAnonymousSignOutConfirming] = useState(false);

  const [nameInput, setNameInput] = useState(game.playerName);
  const [nameError, setNameError] = useState<string | null>(null);

  const submitName = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = nameInput.trim();
    if (trimmed.length < 2 || trimmed.length > 20) {
      setNameError('Name muss 2–20 Zeichen lang sein.');
      return;
    }
    setNameError(null);
    actions.renameCommander(trimmed);
  };

  const submitUpgrade = async (e: FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!isValidEmail(upgradeEmail)) errors.email = 'Bitte eine gültige E-Mail-Adresse eingeben.';
    if (upgradePassword.length < MIN_PASSWORD_LENGTH) {
      errors.password = `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`;
    }
    if (upgradePassword !== upgradePasswordConfirm) errors.passwordConfirm = 'Passwörter stimmen nicht überein.';
    if (Object.keys(errors).length > 0) {
      setUpgradeFieldErrors(errors);
      return;
    }
    setUpgradeSubmitting(true);
    setUpgradeError(null);
    try {
      await auth.upgradeToEmailAccount(upgradeEmail, upgradePassword);
      setUpgradeSuccess(true);
      setUpgradeFormOpen(false);
    } catch (e) {
      setUpgradeError(e instanceof Error ? e.message : 'Konto sichern fehlgeschlagen.');
    } finally {
      setUpgradeSubmitting(false);
    }
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await auth.signOut();
      // Simplest correct way to get AuthGate (top of the tree) to
      // re-evaluate from here — mirrors "Kolonie aufgeben" doing a full
      // local-state reset rather than fine-grained cross-tree surgery.
      window.location.reload();
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <>
      <ViewHeader eyebrow="Einstellungen" title="Kommandozentrale-Einstellungen" />

      <Panel title="Profil" eyebrow="Kommandant">
        <form className="flex items-end gap-3" onSubmit={submitName}>
          <div className="flex-1">
            <TextField
              label="Kommandantenname"
              value={nameInput}
              onChange={setNameInput}
              error={nameError ?? undefined}
              maxLength={20}
            />
          </div>
          <Button type="submit" variant="secondary" size="md" disabled={nameInput.trim() === game.playerName}>
            Speichern
          </Button>
        </form>
      </Panel>

      <Panel title="Hilfe & Tutorial" eyebrow="Nachschlagewerk">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-content">Hilfe-Center</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                Alle Spielsysteme erklärt, mit Suche und häufigen Fragen — jederzeit auch über den
                Fragezeichen-Knopf oben erreichbar.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => tutorial.openHelp()}>
              Öffnen
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-content">Geführte Tour</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                Die kurze Einführung durch das Cockpit, die beim ersten Start angeboten wurde.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => tutorial.startTour()}>
              Erneut starten
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-content">Sprache des Hilfe-Centers</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                Gilt nur für Tutorial, geführte Tour und Hilfe-Center — die restliche Oberfläche
                bleibt Deutsch.
              </p>
            </div>
            <Tabs
              variant="enclosed"
              value={settings.locale}
              onChange={(id) => settingsActions.setLocale(id as 'de' | 'en')}
              items={[
                { id: 'de', label: 'DE' },
                { id: 'en', label: 'EN' },
              ]}
            />
          </div>
        </div>
      </Panel>

      <Panel title="Darstellung" eyebrow="Theme">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-content">Farbschema</div>
            <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
              Rein kosmetisch — keine Auswirkung auf Werte oder Kampfstärke.
            </p>
          </div>
          <Tabs
            variant="enclosed"
            value={settings.theme}
            onChange={(id) => settingsActions.setTheme(id as 'dark' | 'command')}
            items={[
              { id: 'dark', label: 'Dunkel' },
              { id: 'command', label: 'Kommando' },
            ]}
          />
        </div>
      </Panel>

      <Panel title="Barrierefreiheit" eyebrow="Zugänglichkeit">
        <Switch
          id="settings-reduced-motion"
          checked={settings.reducedMotion}
          onChange={settingsActions.setReducedMotion}
          label="Bewegungseffekte reduzieren"
          description="Deaktiviert Reel-Spins, Partikel und andere ausgeprägte Animationen — unabhängig von der Betriebssystem-Einstellung."
        />
      </Panel>

      <Panel title="Spielverhalten" eyebrow="Bestätigungen">
        <Switch
          id="settings-skip-boost-confirm"
          checked={settings.skipBoostConfirm}
          onChange={settingsActions.setSkipBoostConfirm}
          label="Nicht vor jedem Zeitsprung bestätigen"
          description="Chronos-Kristalle für Bauzeit-Verkürzungen werden ohne Rückfrage ausgegeben. Käufe im Shop fragen immer nach — das lässt sich hier nicht abschalten."
        />
      </Panel>

      <Panel title="Ton" eyebrow="Musik & Soundeffekte">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 opacity-50">
            <div>
              <div className="text-sm font-medium text-content">Musik</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">Hintergrundmusik im Kommandozentrum.</p>
            </div>
            <Switch checked={false} onChange={() => {}} disabled />
          </div>
          <div className="flex items-center justify-between gap-4 opacity-50">
            <div>
              <div className="text-sm font-medium text-content">Soundeffekte</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                Bau-Abschlüsse, Casino-Gewinne, Alarme.
              </p>
            </div>
            <Switch checked={false} onChange={() => {}} disabled />
          </div>
          <Badge accent="neutral" size="sm">
            Kommt noch — Ton ist im Spiel bisher nicht eingebunden
          </Badge>
        </div>
      </Panel>

      <Panel title="Konto & Daten" eyebrow={authStatus?.email ?? 'Anonym gespielt'}>
        <div className="space-y-4">
          {authStatus?.mode === 'anonymous' ? (
            <>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-content">Konto verknüpfen</div>
                  <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                    Dein Spielstand liegt bisher nur auf diesem Gerät. Sichere ihn mit Google oder
                    E-Mail + Passwort, damit du dich auch von einem anderen Gerät aus anmelden
                    kannst — dein Fortschritt bleibt dabei erhalten.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => actions.linkGoogleAccount()}>
                    Google verknüpfen
                  </Button>
                  {!upgradeFormOpen ? (
                    <Button variant="secondary" size="sm" onClick={() => setUpgradeFormOpen(true)}>
                      Mit E-Mail sichern
                    </Button>
                  ) : null}
                </div>
              </div>

              {upgradeFormOpen ? (
                <form
                  className="flex flex-col gap-3 rounded-control border border-edge-subtle bg-surface-2 p-3"
                  onSubmit={submitUpgrade}
                >
                  <TextField
                    label="E-Mail"
                    type="email"
                    autoComplete="email"
                    value={upgradeEmail}
                    onChange={setUpgradeEmail}
                    error={upgradeFieldErrors.email}
                  />
                  <TextField
                    label="Passwort"
                    type="password"
                    autoComplete="new-password"
                    value={upgradePassword}
                    onChange={setUpgradePassword}
                    error={upgradeFieldErrors.password}
                  />
                  <TextField
                    label="Passwort bestätigen"
                    type="password"
                    autoComplete="new-password"
                    value={upgradePasswordConfirm}
                    onChange={setUpgradePasswordConfirm}
                    error={upgradeFieldErrors.passwordConfirm}
                  />
                  {upgradeError ? <p className="text-xs text-danger-bright">{upgradeError}</p> : null}
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setUpgradeFormOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit" variant="primary" size="sm" loading={upgradeSubmitting}>
                      Konto sichern
                    </Button>
                  </div>
                </form>
              ) : null}

              {upgradeSuccess ? (
                <p className="text-xs text-success">
                  Konto gesichert — melde dich auf einem anderen Gerät mit diesen Zugangsdaten an,
                  um denselben Spielstand weiterzuspielen.
                </p>
              ) : null}

              <div className="flex items-center justify-between gap-4 border-t border-edge-subtle pt-4">
                <div>
                  <div className="text-sm font-medium text-content">Abmelden</div>
                  <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                    Du spielst als Gast ohne gesichertes Konto. Ohne Google-Verknüpfung oder
                    E-Mail-Absicherung von oben kommst du nach dem Abmelden nicht mehr an diesen
                    Spielstand heran.
                  </p>
                </div>
                {anonymousSignOutConfirming ? (
                  <div className="flex shrink-0 items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setAnonymousSignOutConfirming(false)}>
                      Abbrechen
                    </Button>
                    <Button variant="danger" size="sm" loading={signingOut} onClick={signOut}>
                      Trotzdem abmelden
                    </Button>
                  </div>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => setAnonymousSignOutConfirming(true)}>
                    Abmelden
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-content">Abmelden</div>
                <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                  Meldet dich von diesem Gerät ab. Mit deinen Zugangsdaten kannst du dich jederzeit
                  wieder anmelden.
                </p>
              </div>
              <Button variant="secondary" size="sm" loading={signingOut} onClick={signOut}>
                Abmelden
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-content">Spielstand exportieren</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                Lädt eine JSON-Datei mit dem aktuellen Spielstand herunter.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => downloadSave(JSON.stringify(serialize(game)), game.playerName)}
            >
              Exportieren
            </Button>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-edge-subtle pt-4">
            <div>
              <div className="text-sm font-medium text-danger-bright">Kolonie aufgeben</div>
              <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
                Löscht den Spielstand unwiderruflich und startet eine neue Kolonie.
              </p>
            </div>
            {resetConfirming ? (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setResetConfirming(false)}>
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    actions.resetGame();
                    setResetConfirming(false);
                  }}
                >
                  Wirklich löschen
                </Button>
              </div>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setResetConfirming(true)}>
                Zurücksetzen
              </Button>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="Info" eyebrow="Über das Spiel">
        <p className="text-xs leading-relaxed text-content-muted">
          Dyson Warfare — persistentes Sci-Fi-Weltraumspiel. Alle Angaben ohne Gewähr auf
          Balance-Endstand, das Spiel wird laufend weiterentwickelt.
        </p>
        <p className="mt-2 text-xs text-content-muted" data-sg-numeric>
          Version {BUILD_INFO.gitSha} · gebaut am {formatBuildTime(BUILD_INFO.buildTime)}
        </p>
      </Panel>
    </>
  );
}
